import { authenticatedFetch } from './auth';

// Address CRUD operations
export const addressAPI = {
  // Get all addresses (use context instead for cached version)
  async getAll() {
    const response = await authenticatedFetch('/addresses');
    if (!response.ok) {
      throw new Error('Failed to fetch addresses');
    }
    return await response.json();
  },

  // Add new address
  async add(addressData) {
    const response = await authenticatedFetch('/addresses', {
      method: 'POST',
      body: JSON.stringify({
        address: addressData.address,
        label: addressData.label,
        input_type: addressData.inputType || 'address',
        place_id: addressData.place_id
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to add address');
    }

    return await response.json();
  },

  // Update address
  async update(id, addressData) {
    const response = await authenticatedFetch(`/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        address: addressData.address,
        label: addressData.label,
        input_type: addressData.inputType || 'address',
        place_id: addressData.place_id
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update address');
    }

    return await response.json();
  },

  // Delete address
  async delete(id) {
    const response = await authenticatedFetch(`/addresses/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error('Failed to delete address');
    }
  },

  // Upload CSV
  async uploadCSV(file, mode = 'append') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);

    const response = await authenticatedFetch('/addresses/upload-csv', {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type, let browser set it with boundary
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to upload CSV');
    }

    return await response.json();
  }
};

// Address filtering and search utilities
export const addressUtils = {
  // Filter addresses by search term
  filterBySearch(addresses, searchTerm) {
    if (!searchTerm) return addresses;
    
    const term = searchTerm.toLowerCase();
    return addresses.filter(addr => 
      addr.address.toLowerCase().includes(term) ||
      addr.label.toLowerCase().includes(term)
    );
  },

  // Get addresses for dropdown (limited results)
  getDropdownAddresses(addresses, searchTerm, limit = 10) {
    return this.filterBySearch(addresses, searchTerm).slice(0, limit);
  },

  // Find address by ID
  findById(addresses, id) {
    return addresses.find(addr => addr.id === id);
  },

  // Find address by address string
  findByAddress(addresses, address) {
    return addresses.find(addr => addr.address === address);
  },

  // Sort addresses by label
  sortByLabel(addresses) {
    return [...addresses].sort((a, b) => a.label.localeCompare(b.label));
  },

  // Validate address data
  validateAddress(addressData) {
    const errors = [];
    
    if (!addressData.address?.trim()) {
      errors.push('Address is required');
    }
    
    if (!addressData.label?.trim()) {
      errors.push('Label is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

// Keyboard navigation utilities
export const keyboardNavigation = {
  handleKeyDown(event, options) {
    const {
      dropdownOpen,
      highlightedIndex,
      filteredItems,
      onArrowDown,
      onArrowUp,
      onEnter,
      onEscape,
      onSelect
    } = options;

    if (!dropdownOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        onArrowDown((prev) => (prev + 1) % filteredItems.length);
        break;
      
      case 'ArrowUp':
        event.preventDefault();
        onArrowUp((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
        break;
      
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          onSelect(filteredItems[highlightedIndex]);
        }
        break;
      
      case 'Escape':
        onEscape();
        break;
    }
  }
};

// Click outside handler
export const clickOutsideHandler = {
  createHandler(options) {
    const {
      dropdownRef,
      inputRef,
      onClose
    } = options;

    return (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        onClose();
      }
    };
  }
};
